import { useCallback, useEffect, useRef, useState } from "react";
import type { DevicePresence, DisplayCommand, GoonSession } from "@gooner/shared";
import { type ControlConn, connectControl } from "../../lib/ws";

/**
 * One control socket for the home screen: live presence per device, and the channel a
 * retarget nudges a screen down.
 *
 * The hello carries identity MATERIAL and the server resolves it, so this socket only
 * ever hears about this operator's own devices — it states no owner id, because a stated
 * one would make the ownership check on the other side worthless. Opened once for the
 * life of the screen — `connectControl` reconnects on its own, so this effect must not be
 * keyed on anything that changes, or a reconnect loop is one dependency away.
 *
 * `ready` starts false and only turns true when a snapshot actually arrives. An empty map
 * is not an answer: before the first snapshot nothing is known to be online, which is not
 * the same as everything being offline, and the two must never be confused (see rail.ts —
 * absence never starts a death clock).
 */
export function useDevicePresence(
  /**
   * Told about the operator's goon session whenever it changes — an edge added on a
   * screen, a clip counted on the wall, a session started from another remote. Held in a
   * ref so passing an inline lambda cannot tear the socket down and rebuild it.
   */
  onSession?: (session: GoonSession | null) => void,
): {
  presence: Record<string, DevicePresence>;
  ready: boolean;
  connected: boolean;
  sendCommand: (deviceId: string, cmd: DisplayCommand) => void;
} {
  const [presence, setPresence] = useState<Record<string, DevicePresence>>({});
  const [ready, setReady] = useState(false);
  const [connected, setConnected] = useState(false);
  const connRef = useRef<ControlConn | null>(null);
  const onSessionRef = useRef(onSession);
  onSessionRef.current = onSession;

  useEffect(() => {
    const conn = connectControl({
      onStatus: setConnected,
      onPresence: (devices) => {
        setPresence(devices);
        setReady(true);
      },
      onSession: (session) => onSessionRef.current?.(session),
    });
    connRef.current = conn;
    return () => {
      conn.close();
      connRef.current = null;
    };
  }, []);

  // STABLE identity, because callers key effects on it. As a fresh arrow per render,
  // the Goontroller's identify pulse tore down and re-armed on every render — the TV
  // got off/on churn (a visible blink) and the pulse interval never reached its first
  // tick. Reading only the ref, it can never go stale, so the empty deps are honest.
  const sendCommand = useCallback(
    (deviceId: string, cmd: DisplayCommand) =>
      connRef.current?.sendCommand(deviceId, cmd),
    [],
  );

  return { presence, ready, connected, sendCommand };
}
